import { COMPUTE_MODE, DATA_KIND } from '../../../model/enums';
import { FunctionObj } from '../../../vo/workingdata/function-obj';
import { FunctionCoreEngine } from '../function.core.engine';
import { FormulaCoreEngine } from '../formula.core.engine';
import { BaselineEngine } from '../../workingdata/baseline.engine';
import { FeiVO } from '../../../vo/workingdata/fei.vo';
import { WizConstants } from '../../../model/constants';
import { WidgetCell, WidgetLine } from '../../../vo/workingdata/widget-line.obj';
import { AkmFDVCell, IBaselineValue, KeyPair, SisterResult } from '../../../model/interfaces';
import { FeiKeyDef } from '../../../vo/workingdata/fei-key-definition.obj';
import { CompareManager } from '../../baselines/compare.manager';
import { MeasModeItem } from '../measure.mode.obj';
import { WDSSortField, WizCollectionUtils } from '@wizeoo/akm-core-lib';
import { Model } from '../../../model/model';
import { Measure } from '@wizeoo/akm-message-lib';

/**
 * fonctions de gestion de collections
 */
export class CollectionFunctions {


  /**
   * création de la liste des fonctions
   * @param model 
   * @returns 
   */
  public static functions(model: Model): FunctionObj[] {
    const funcArr: FunctionObj[] = [];
    let func: FunctionObj;

    // COLLECT, aggrégat
    func = new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.COLLECT_KEY, [DATA_KIND.MEASURE]);
    func.isAggregate = true;
    funcArr.push(func);

    // SUBCOLLECT, aggrégat
    func = new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.SUBCOLLECT_KEY, [DATA_KIND.MEASURE, DATA_KIND.NUMBER]);
    func.isAggregate = true;
    funcArr.push(func);

    // SLIDEAGG_KEY, aggrégat
    func = new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.SLIDEAGG_KEY, [DATA_KIND.STRING, 
      DATA_KIND.MEASURE, DATA_KIND.STRING, DATA_KIND.MEASURE, DATA_KIND.MEASURE], false);
    func.isAggregate = false;
    funcArr.push(func);

    // FROMAGG, aggrégat
    func = new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.FROMAGG_KEY, [DATA_KIND.STRING, DATA_KIND.MEASURE, DATA_KIND.STRING, DATA_KIND.NUMBER]);
    func.isAggregate = false;
    funcArr.push(func);

    // LIST, aggrégat
    func = new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.LIST_KEY, [DATA_KIND.MEASURE, DATA_KIND.STRING], false, [DATA_KIND.MEASURE, DATA_KIND.STRING, DATA_KIND.BOOLEAN]);
    func.isAggregate = true;
    funcArr.push(func);

    // SISTERS, aggrégat de même niveau
    func = new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.SISTERS_KEY, [DATA_KIND.MEASURE, DATA_KIND.STRING]);
    func.isAggregate = false;
    funcArr.push(func);

    // GROUPVAL, comme un somme.si
    func = new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.GROUPVAL_KEY, [DATA_KIND.MEASURE, DATA_KIND.STRING, DATA_KIND.MEASURE], true);
    func.isAggregate = false;
    funcArr.push(func);

    // FOLKS, aggrégat de même niveau, uniquement les soeurs (même parent)
    func = new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.FOLKS_KEY, [DATA_KIND.MEASURE, DATA_KIND.STRING]);
    func.isAggregate = false;
    funcArr.push(func);

    // COLLECTLIST, aggrégat sur une liste
    func = new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.COLLECT_LIST_KEY, [DATA_KIND.MEASURE]);
    func.isAggregate = false;
    func.isList = true;
    funcArr.push(func);

    funcArr.push(new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.SPLIT_KEY, [DATA_KIND.MEASURE, DATA_KIND.WEIGHT], false, [DATA_KIND.MEASURE, DATA_KIND.NUMBER]));
    funcArr.push(new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.INHERIT_KEY, [DATA_KIND.MEASURE]));
    funcArr.push(new FunctionObj(model, FunctionObj.KIND_COLLECTION, FunctionCoreEngine.ANCESTOR_KEY, [DATA_KIND.MEASURE, DATA_KIND.NUMBER]));
    funcArr.push(new FunctionObj(model, FunctionObj.KIND_COLLECTION, 'SERIES', [DATA_KIND.STRING, DATA_KIND.NUMBER, DATA_KIND.NUMBER, DATA_KIND.NUMBER], false));
    funcArr.push(new FunctionObj(model, FunctionObj.KIND_COLLECTION, 'FIRST', [DATA_KIND.NUMBER]));
    funcArr.push(new FunctionObj(model, FunctionObj.KIND_COLLECTION, 'LAST', [DATA_KIND.NUMBER]));


    funcArr.push(new FunctionObj(model, FunctionObj.KIND_COLLECTION, 'LASTVALUEOF', [DATA_KIND.STRING, DATA_KIND.MEASURE]));
    funcArr.push(new FunctionObj(model, FunctionObj.KIND_COLLECTION, 'LASTINDEXOF', [DATA_KIND.STRING, DATA_KIND.MEASURE]));
    funcArr.push(new FunctionObj(model, FunctionObj.KIND_COLLECTION, 'COMPARERANGE', [DATA_KIND.STRING, DATA_KIND.NUMBER, DATA_KIND.STRING, DATA_KIND.MEASURE, DATA_KIND.MEASURE]));
    funcArr.push(new FunctionObj(model, FunctionObj.KIND_COLLECTION, 'FALSETWINS', [DATA_KIND.STRING, DATA_KIND.NUMBER, DATA_KIND.STRING, DATA_KIND.STRING, DATA_KIND.MEASURE, DATA_KIND.MEASURE]));

    return funcArr;
  }

    /**
     * Ajout massif de cellules à la map des calculs en attente (one shot)
     * sur INHERIT & ANCESTOR (prise du supérieur)
     *
     * @param targetFEI porte les widgetLines à calculer
     * @param targetMeasure mesure à calculer
     * @param sourceLevel  niveau cible du FEI porteurs de l'information
     * @param sourceMeasure mesure sur la source pour le calcul
     * @returns rien
     */
    private static addMassPending(targetFEI: FeiVO, targetMeasure: string,  requiredLevel: number, requiredMeasure: string): void {
 
      // map clé Mère = Map<clé fille, WidgetLine>
      let byMotherMap: Map<string, Map<string, WidgetLine>> = targetFEI.getAllWidgetLinesByLevel(targetFEI.scenario, requiredLevel);

      // map des pendings à alimenter : map clé fille = Map< mesure, WidgetCell>
      // on est déjà par requiredLevel, scen, targetLevel
      let pendings: Map<string, Map<string, WidgetCell>> = targetFEI.model.cellMatrixEngine.checkPendingMapExist(requiredLevel, targetFEI.visionStepLevel, targetFEI.scenario);

      let motherValue: IBaselineValue;
      let childValue: IBaselineValue;
      let childCell: WidgetCell;

      [...byMotherMap.keys()].forEach((motherKey: string) =>{
        // on ajoute l'entrée IBaselineValue pour la cellule mère
        motherValue = targetFEI.model.cellMatrixEngine.addCell(requiredLevel, motherKey, targetFEI.scenario, requiredMeasure, null, true);
        
        // on construit les dépendances pour toutes les filles
        [...byMotherMap.get(motherKey).values()].forEach((wl: WidgetLine) => {
          childValue = targetFEI.model.cellMatrixEngine.addCell(wl.level, wl.key, wl.scen, targetMeasure, null, true);
          childCell = targetFEI.model.cellMatrixEngine.getWidgetCell(wl, targetMeasure);
          if(!childCell) {
            console.log('wtf', wl);
          }
          targetFEI.model.cellMatrixEngine.addDependancy(motherValue, childCell); // ajout de la dépendance sur la mère

          // ajout dans la map des pendings
          if(!pendings.has(wl.key)) { 
            pendings.set(wl.key, new Map());
          }
          pendings.get(wl.key).set(targetMeasure, childCell);

        });
      });
    }

  /**
   * retourne le FEI de même scenario au step cible (inherit, ancestor, collect...)
   * @param sourceFEI mon fei
   * @param targetLevel l'ancêtre que je cherche
   * @returns l'ancêtre
   */
  private static getOtherFEI(sourceFEI: FeiVO, targetLevel: number): FeiVO {

    let scen: string = sourceFEI.scenario;
    if(sourceFEI.scenario === WizConstants.SCEN_FAKE_KEY_CODE) {
      scen = WizConstants.BASE_SCENARIO;
    }

    let motherFEI: FeiVO = sourceFEI.model.dataEngine.getFei(COMPUTE_MODE.LOCAL, targetLevel, scen) || null;
    return motherFEI;
  }

    /* ################################################
            héritages du dessus
     ################################################ */

  /**
   * gestion de la répartition par héritage complet (= DADDY)
   * @param1 : la mesure cible sur le parent
   */
  public static INHERIT(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {

    if (line.level <= 1) {  // j'ai pas de parent...
      mmi.r = '';
      return;
    }

    const requiredLevel: number = fei.visionStepLevel - 1;
    const scenario: string = line.scen;
    const measureKey: string = mmi.c[0].form; // la mesure collectée

    // console.log('INHERIT', fei.scenario, scenario, line);
    // if(line.key === 'TEN2_102|TEN2_102_101') {
    //   console.log('trap you');
    // }

    // fei cible
    let motherFEI: FeiVO = this.getOtherFEI(fei, requiredLevel);
    if (!motherFEI?.computed) { // pas chargé... on inscrit les cellules en pending
      mmi.r = ''; // j'ai pas ENCORE de parent...
      // faire ça une seule fois, comme les setCollections...
      if(!fei.model.cellMatrixEngine.pendingExist(requiredLevel, scenario, line.level, line.key, mmi.mm.code)) { 
        this.addMassPending(fei, mmi.mm.code, requiredLevel, measureKey);
      }
      return;
    }

    // baseline parente
    const motherBaseline: WidgetLine = motherFEI.getWidgetLineForKey(scenario, line.bl.pkey); 

    if (!motherBaseline) {
      mmi.isErr = true;
      mmi.r = ''; // j'ai VRAIMENT pas de parent...
      return;
    }

    // let cell: WidgetCell = line.cells.get(mmi.mm.code);
    let cell: WidgetCell = fei.model.cellMatrixEngine.getWidgetCell(line, mmi.mm.code); // fix nullPointer sur mesure par défaut dans les forms sur #S
    // if(line.level === 2 && line.scen === 'BAD') {
    //   console.log('track me', line, line.scen, cell);
    // }

    // prise de la valeur sur la BL, possible override
    let motherCell: WidgetCell = motherBaseline.cells.get(measureKey);
    mmi.r = fei.model.cellMatrixEngine.getCellValueAndAddDependency(motherBaseline.level, motherBaseline.key, motherCell?.sc || motherBaseline.scen, measureKey, cell);

    // mmi.result = fei.model.cellMatrixEngine.getCellValueAndAddDependency(motherBaseline.level, motherBaseline.key, motherBaseline.bl.scen, measureKey, cell);
  }

  /**
   * gestion de la répartition par héritage complet (= DADDY)
   * @param1 : la mesure cible sur l'ancêtre
   * @param2 : le niveau de l'ancêtre
   * @param mmi      Etape de calcul concernée
   * @param line: WidgetLine de travail concernée
   * @param fei      Moteur de calcul
   */
  public static ANCESTOR(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {

    const measureKey: string = mmi.c[0].form; // la mesure cible sur l'ancêtre
    const requiredLevel: number = Number(mmi.c[1].form); // le level de l'ancêtre
    const scenario: string = line.scen;

    if (requiredLevel < 1) {
      mmi.r = ''; // j'ai pas de parent...
      return;
    }

    if (requiredLevel >= fei.visionStepLevel) {
      mmi.r = ''; // c'est moi ou un enfant...
      return;
    }

    // fei cible
    let motherFEI: FeiVO = this.getOtherFEI(fei, requiredLevel);
    if (!motherFEI?.computed) { // retour, pas chargé...
      mmi.r = ''; // j'ai pas ENCORE de parent...

      // faire ça une seule fois, comme les setCollections...
      if(!fei.model.cellMatrixEngine.pendingExist(requiredLevel, scenario, line.level, line.key, mmi.mm.code)) { 
        this.addMassPending(fei, mmi.mm.code, requiredLevel, measureKey);
       }
      return;
    }

    // construction de la clé cible (pour le niveau cible)
    if (!line.bl.splittedKey) {
      line.bl.splittedKey = line.key.split(WizConstants.KEY_SEPARATOR);
    }
    // ma clé équivalente à ce niveau
    const motherKey: string = line.bl.splittedKey.slice(0, fei.vision.keySizeByLevel.get(requiredLevel)).join(WizConstants.KEY_SEPARATOR);
    // baseline parente
    const motherBaseline: WidgetLine = motherFEI.getWidgetLineForKey(line.scen, motherKey); //  source.baselines[motherKey];

    if (!motherBaseline) {
      mmi.isErr = true;
      mmi.r = ''; // j'ai VRAIMENT pas de parent...
      // console.log('noParent on ancestor', line, measureKey, requiredLevel);
      return;
    }

    // let cell: WidgetCell = line.cells.get(mmi.mm.code);
    let cell: WidgetCell = fei.model.cellMatrixEngine.getWidgetCell(line, mmi.mm.code); // fix nullPointer sur mesure par défaut dans les forms sur #S
    

    // prise de la valeur sur la BL, possible override
    let motherCell: WidgetCell = motherBaseline.cells.get(measureKey);
    mmi.r = fei.model.cellMatrixEngine.getCellValueAndAddDependency(motherBaseline.level, motherBaseline.key, motherCell?.sc || motherBaseline.scen, measureKey, cell);

    // mmi.result = fei.model.cellMatrixEngine.getCellValueAndAddDependency(motherBaseline.level, motherBaseline.key, motherBaseline.bl.scen, measureKey, cell);
 }

    /* ################################################
            collections du dessous
     ################################################ */

  /**
   * consolidation des lignes enfants (pour sum, par exemple)
   * Alimente le collectContent sur le mmi (collection de string)
   * @param1 : la mesure cible sur les enfants
   * @param mmi      Etape de calcul concernée
   * @param line: WidgetLine de travail concernée
   * @param fei      Moteur de calcul
   */
  public static COLLECT(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {
    const measureKey: string = mmi.c[0].form; // la mesure à sommer  

    let nbStep: number = BaselineEngine.getVisionStepCount(fei.vision); // pas de sommes si pas de step suivant
    if (line.level >= nbStep) {
      mmi.cc = []; // j'ai pas d'enfants...
      return;
    }

    // step cible
    const requiredLevel: number = fei.visionStepLevel + 1; 

    // la cellule qui porte le collect. Attention, son scenario n'est peut-être pas celui de la ligne (héritage)
    let cell: WidgetCell = fei.model.cellMatrixEngine.getWidgetCrossStepCell(line, mmi.mm.code);

    // fei cible
    let childFEI: FeiVO = this.getOtherFEI(fei, requiredLevel);
    if (!childFEI?.computed) { // retour, pas chargé... ou pas calculé
      mmi.cc = []; // j'ai pas ENCORE d'enfants...
      
      // on s'inscrit dans la map des pendings
      fei.model.cellMatrixEngine.addPendingCell(requiredLevel, cell); 
      return;
    }

    const childLines: Map<string, WidgetLine> = childFEI.getWidgetLinesByLevel(line.scen, line.level, cell.k) || new Map();
    const childrenValues: string[] = []; // la collection de valeurs collectées sur les enfants

    if(!fei.model.cellMatrixEngine.neoCollectionExist(cell, requiredLevel, line.scen, measureKey)) {
      // en cas de mesure CALCULE, mettre la deps sur le scen, pas sur l'origine
      let targetMeas: Measure = childFEI.matrix.get(measureKey).m;
      let myScenOrOrigin: string = line.scen
      if(!targetMeas.editable) {
        myScenOrOrigin = cell.sc;
      }
      if(line)
      fei.model.cellMatrixEngine.setNeoCollectionDependencies(cell, requiredLevel, myScenOrOrigin, [...childLines.values()], measureKey);    
    }



    // TODO TODO mettre refresh sur cellule d'origine... en cas d'override ?
    // pas de refresh si modif du scenar MAIN...

    let childCellScen: string;
    [...childLines.values()].forEach((child: WidgetLine) => {
      // important de prendre le scenar sur la cellule, possible override
      childCellScen = child.cells?.get(measureKey)?.sc || child.scen;
      let val = fei.model.cellMatrixEngine.getCellValue(requiredLevel, child.key, childCellScen, measureKey);

      childrenValues.push(val);
    });

    // if(cell.level === 1) {
    //   console.log('collect', childrenValues);
    // }
    
    mmi.cc = childrenValues;
  }

  /**
   * consolidation des informations d'un step inférieur (pas juste mes enfants)
   * Alimente le collectContent sur le mmi (collection de string)
   * @param mmi      Etape de calcul concernée
   * @param line: WidgetLine de travail concernée
   * @param fei      Moteur de calcul
   */
  public static SUBCOLLECT(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {
    const measureKey: string = mmi.c[0].form;
    const requiredLevel: number = Number(mmi.c[1].form);
    const scenario: string = line.scen;
    const childrenValues: string[] = [];

    // console.log('SUBCOL', fei.scenario, scenario, line);

    if (requiredLevel > BaselineEngine.getVisionStepCount(fei.vision) || requiredLevel <= fei.visionStepLevel) {
      mmi.cc = []; // j'ai pas d'enfants...
      return;
    }

    // la cellule qui porte le collect. Attention, son scenario n'est peut-être pas celui de la ligne (héritage)
    // let cell: WidgetCell = fei.model.cellMatrixEngine.getWidgetCell(line, mmi.mm.code);
    let cell: WidgetCell = fei.model.cellMatrixEngine.getWidgetCrossStepCell(line, mmi.mm.code);
    // fei cible
    let childFEI: FeiVO = this.getOtherFEI(fei, requiredLevel);
    if (!childFEI?.computed) { // retour, pas chargé...
      mmi.cc = []; // j'ai pas ENCORE d'enfants...
      fei.model.cellMatrixEngine.addPendingCell(requiredLevel, cell);
      return;
    }

    const childLines: Map<string, WidgetLine> = childFEI.getWidgetLinesByLevel(scenario, line.level, line.key) || new Map();

    if(!fei.model.cellMatrixEngine.neoCollectionExist(cell, requiredLevel, line.scen, measureKey)) {
      let targetMeas: Measure = childFEI.matrix.get(measureKey)?.m;
      let myScenOrOrigin: string = line.scen
      if(!!targetMeas) { // au cas ou la mesure ne serait pas définie sur le step cible
        if(!targetMeas.editable) {
          myScenOrOrigin = cell.sc;
        }
      }
      try {
        fei.model.cellMatrixEngine.setNeoCollectionDependencies(cell, requiredLevel, myScenOrOrigin, [...childLines.values()], measureKey);
      } catch(e: any) {
        console.log('LMA TRACE subCollect error', cell, requiredLevel, myScenOrOrigin, [...childLines?.values() || []], measureKey);
      }
      
    }

    let childCellScen: string;
    [...childLines.values()].forEach((child: WidgetLine) => {
      // importante de prendre le scenar sur la cellule, possible override
      childCellScen = child.cells?.get(measureKey)?.sc || child.scen;
      childrenValues.push(fei.model.cellMatrixEngine.getCellValue(requiredLevel, child.key, childCellScen, measureKey));
    });

    // alimentation de la collection de content pour le Collect
    mmi.cc = childrenValues;
  }

  /**
   * rendu liste d'une sous-collection
   * @param1 : la mesure cible sur les enfants, MEAS_TEST#I, SANS les guillements
   * @param2 : les clés de Key/ filter pour construire les libellés "FILT#1|MYKEY#1"
   * @param3 noZero Boolean (0/1), pour ne pas afficher les résultats nuls 
   */
  public static LIST(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {

    if (line.level >= BaselineEngine.getVisionStepCount(fei.vision)) {
      mmi.r = null; // j'ai pas d'enfants...
      return;
    }

    const measureKey: string = mmi.c[0].form; // la mesure que l'on va rechercher
    const keyCodesRaw: string = mmi.c[1].form.replace(new RegExp('"', 'g'), ''); // les champs pour le rendu, séparés par |
    const noZero: boolean = !!mmi.c[2]?.form; // si 1, pas de prise en compte des résults = 0

    const requiredLevel: number = fei.visionStepLevel + 1;
    const scenario: string = fei.scenario;

    // la cellule qui porte le collect. Attention, son scenario n'est peut-être pas celui de la ligne (héritage)
    let cell: WidgetCell = fei.model.cellMatrixEngine.getWidgetCell(line, mmi.mm.code);
    // console.log('LIST pendings', requiredLevel, measureKey, keyCodesRaw);
    // construction de la chaine de clé pour rendu
    // TODO non, sur le FEI cible...
    // let KeyCodesDef: FeiKeyDef[] = [];
    // keyCodesRaw.split(WizConstants.KEY_SEPARATOR).forEach((key: string) => {
    //   let keyDef: FeiKeyDef = fei.keyMap.get(key.split(WizConstants.FD_PARTS_SEPARATOR)[0]);
    //   if(!!keyDef) {
    //     KeyCodesDef.push(keyDef);
    //   }
    // });

    // if(KeyCodesDef.length <= 0) { // pas de champs pour la clé de rendu
    //   mmi.r = null; 
    //   return;
    // }
    
    // prise de la mesure
    let measDef: FeiKeyDef =  fei.keyMap.get(measureKey);
    if(!measDef) { // mauvaise mesure
      mmi.r = null; 
      return;
    }

    // fei cible
    let childFEI: FeiVO = this.getOtherFEI(fei, requiredLevel);
    if (!childFEI?.computed) { // retour, pas chargé...
      mmi.cc = []; // j'ai pas ENCORE d'enfants...
      fei.model.cellMatrixEngine.addPendingCell(requiredLevel, cell);
      return;
    }

    // console.log('LIST', childFEI, line.key);

    // prise des lignes enfants
    const childLines: Map<string, WidgetLine> = childFEI.getWidgetLinesByLevel(scenario, fei.visionStepLevel, line.key) || new Map();

    if(!fei.model.cellMatrixEngine.neoCollectionExist(cell, requiredLevel, line.scen, measureKey)) {
      // console.log('LIST neoColl', cell, childLines.values());
      fei.model.cellMatrixEngine.setNeoCollectionDependencies(cell, requiredLevel, line.scen, [...childLines.values()], measureKey);
    }
    
    let KeyCodesDef: FeiKeyDef[] = [];
    keyCodesRaw.split(WizConstants.KEY_SEPARATOR).forEach((key: string) => {
      let keyDef: FeiKeyDef = childFEI.keyMap.get(key.split(WizConstants.FD_PARTS_SEPARATOR)[0]);
      if(!!keyDef) {
        KeyCodesDef.push(keyDef);
      }
    });

    if(KeyCodesDef.length <= 0) { // pas de champs pour la clé de rendu
      mmi.r = null; 
      return;
    }


    let keyPairs: KeyPair[] = []; // on va construire des triplettes

    let rawVal: string;
    let childCellScen: string;
    [...childLines.values()].forEach((child: WidgetLine) => {
      childCellScen = child.cells?.get(measureKey)?.sc || child.scen;
      rawVal = fei.model.cellMatrixEngine.getCellValue(requiredLevel, child.key, childCellScen, measureKey);
      if (rawVal?.length > 0) { // j'ai une valeur pour cette ligne
        if(noZero && Number(rawVal) === 0) {
          // pas pris en compte
        } else {
          let keyPair: KeyPair = {r: rawVal, l: null, s: null};
          KeyCodesDef.forEach((fkd: FeiKeyDef) => { // élément de rendu
            let fdvCell: AkmFDVCell = BaselineEngine.checkFDVCell(childFEI, child.bl, fkd.fullCode, WizConstants.FIELD_LABEL);
            if(!!fdvCell) {
              if(!keyPair.s) { // construction de la clé de tri
                keyPair.s = fdvCell.sortKey;
              }
    
              if(!keyPair.l) { // construction du libellé
                keyPair.l = fdvCell.label;
              } else {
                keyPair.l = [keyPair.l, fdvCell.label].join(WizConstants.NICE_KEY_SEPARATOR);
              }
            }
          });
  
          if(!!keyPair.l) { // a minima un morceau de label
            keyPair.r = fei.model.formatEngine.format(keyPair.r, measDef.datakind, measDef.defaultFormat); 
            keyPairs.push(keyPair);
          }
        }
      }
    });

    // resultat final
    let result: string = null;
    if (keyPairs.length > 0) {
      
      const sortField: WDSSortField = { property: 's', numeric: fei.model.dataEngine.datakindIsNumeric(KeyCodesDef[0].datakind), way: 'asc' };
      keyPairs = WizCollectionUtils.doSort(keyPairs, [sortField]);
      let parts: string[] = []; // les combis label : valeur
      keyPairs.slice(0,10).forEach((keyPair: KeyPair) => {
        parts.push([keyPair.l, keyPair.r].join(WizConstants.KEY_PAIR_SEPARATOR))
      });
      result = parts.join(WizConstants.STYLE_ELEMENT_SEPARATOR + ' ');
    }

    mmi.r = result;
  }

  /* ################################################
          sisters (même niveau)
    ################################################ */

  /**
   * consolidation des lignes soeurs (pour sum, par exemple)
   * Alimente le collectContent sur le mmi (collection de string)
   * @param1 : la mesure cible sur les enfants
   * @param2 : la fonction d'aggrégat
   * @param mmi      Etape de calcul concernée
   * @param line: WidgetLine de travail concernée
   * @param fei      Moteur de calcul
   */
  public static SISTERS(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {
    // retour par défaut
    mmi.r = null;
 
    if(line.level === 1) { // pas de soeurs au 1er niveau
      return;
    }

    // la mesure cible à collecter sur les soeurs
    const measureKey: string = mmi.c[0].form; 
    // on check que ça existe
    let fkd: FeiKeyDef = fei.keyMap.get(measureKey);
    if(!fkd) {
      return;
    }
    
    // la fonction d'aggrégat à jouer
    let aggregateKey: string = mmi.c[1].form?.replace(new RegExp('"', 'g'), ''); 
    if (!aggregateKey) {
      if(fei.model.dataEngine.datakindIsNumeric(fkd.datakind)) {
        aggregateKey = 'SUM';
      } else {
        aggregateKey = 'COUNT';
      }
    }

    let measureFullKey: string = [measureKey, aggregateKey].join(WizConstants.KEY_SEPARATOR);
    // la clé de regroupement des soeurs, ventilation par pkey
    let sisKey: string = line.bl.pkey;
    // regarde si c'est déjà calculé dans la matrice
    let exist: boolean = fei.model.cellMatrixEngine.exitSisterResult(fei.visionStepLevel, line.scen, measureFullKey, line.bl.pkey);
    if(!exist) {

      // créer uniquement le nécessaire (ma série, pas les cousines)
      let byKeySisters: Map<string, WidgetLine[]> = new Map();
      byKeySisters.set(sisKey, []);

      fei.getWidgetLinesByLevel(fei.scenario, fei.visionStepLevel - 1, sisKey).forEach((wl: WidgetLine) => {
        byKeySisters.get(wl.bl.pkey).push(wl);
        // console.log('lvl2', wl.bl.pkey, wl.bl);
      });

      let sistersMap: Map<string, SisterResult> = new Map();

      // calculer et construire
      [...byKeySisters.keys()].forEach((pKey:string) => {
        let sistersVals: string[] = [];
        let sisterCells: WidgetCell[] = [];
        let cell: WidgetCell;
        let val: string = null;
        byKeySisters.get(pKey).forEach((wl:WidgetLine) => {
          // la cellule cible du calcul
          // cell = fei.model.cellMatrixEngine.addCell(wl.level, wl.key, wl.scen, mmi.measureMode.code, null, true);
          cell = fei.model.cellMatrixEngine.getWidgetCell(wl, mmi.mm.code);
          sisterCells.push(cell);
          // wl.bl.scen, en cas d'override
          val = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, measureKey);

          // if(sisKey === '100|CHALT|101|101_100') {
          //   console.log('sisKey', line.key, measureKey, val);
          // }

          sistersVals.push(val);

        });

        // on recycle le mmi en cours... pas bien
        FunctionCoreEngine.callFunction(aggregateKey, mmi, line, fei, sistersVals);
    
        let sisterResult: SisterResult = {
          l: line.level, sc: line.scen, magg: measureFullKey, sk: sisKey,
          r: mmi.r, count: sistersVals.length, sis: sisterCells, mk: measureKey };
        sistersMap.set(pKey, sisterResult);
      });

      fei.model.cellMatrixEngine.setNeoSistersDependencies(fei.visionStepLevel, line.scen, measureFullKey, sistersMap);
    }
    
    // je collecte le résultat
    let sistersResult: SisterResult = fei.model.cellMatrixEngine.getSisterResultByPkey(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    mmi.r = sistersResult?.r;
  }

  /**
   * consolidation de toutes les lignes du niveau (pour sum, par exemple)
   * Alimente le collectContent sur le mmi (collection de string)
   * @param1 : la mesure cible sur les enfants
   * @param2 : la fonction d'aggrégat
   * @param mmi      Etape de calcul concernée
   * @param line     WidgetLine de travail concernée
   * @param fei      Moteur de calcul
   */
  public static FOLKS(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {
    
    // retour par défaut
    mmi.r = null;
    
    // la mesure cible à collecter sur les soeurs
    const measureKey: string = mmi.c[0].form; 
    // on check que ça existe
    let fkd: FeiKeyDef = fei.keyMap.get(measureKey);
    if(!fkd) {
      return;
    }
    
    // la fonction d'aggrégat à jouer
    let aggregateKey: string = mmi.c[1].form?.replace(new RegExp('"', 'g'), ''); 
    if (!aggregateKey) {
      if(fei.model.dataEngine.datakindIsNumeric(fkd.datakind)) {
        aggregateKey = 'SUM';
      } else {
        aggregateKey = 'COUNT';
      }
    }

    let measureFullKey: string = [measureKey, aggregateKey].join(WizConstants.KEY_SEPARATOR);
    let sisKey: string = WizConstants.wildcardDomain;
    // regarde si c'est déjà calculé dans la matrice
    let exist: boolean = fei.model.cellMatrixEngine.exitSisterResult(fei.visionStepLevel, fei.scenario, measureFullKey, sisKey);
    if(!exist) {

      // une seule série à la fois, mais c'est tout le monde...
      let byKeySisters: Map<string, WidgetLine[]> = new Map();
      byKeySisters.set(sisKey, []);

      let sistersMap: Map<string, SisterResult> = new Map();

      let sistersVals: string[] = [];
      let sisterCells: WidgetCell[] = [];
      let cell: WidgetCell;
      let val: string = null;
      fei.getAllLines().forEach((wl: WidgetLine) => {
        // la cellule cible du calcul
        // cell = fei.model.cellMatrixEngine.addCell(wl.level, wl.key, wl.scen, mmi.measureMode.code, null, true);
        cell = fei.model.cellMatrixEngine.getWidgetCell(wl, mmi.mm.code);
        
        
        sisterCells.push(cell);
        val = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, measureKey);
        sistersVals.push(val);
        
        byKeySisters.get(sisKey).push(wl);
      });

      // on recycle le mmi en cours... pas bien
      FunctionCoreEngine.callFunction(aggregateKey, mmi, line, fei, sistersVals);
  
      let sisterResult: SisterResult = {
        l: line.level, sc: line.scen, magg: measureFullKey, sk: sisKey,
        r: mmi.r, count: sistersVals.length, sis: sisterCells, mk: measureKey };
      sistersMap.set(sisKey, sisterResult);
    
      fei.model.cellMatrixEngine.setNeoSistersDependencies(fei.visionStepLevel, line.scen, measureFullKey, sistersMap);
    }
    
    // je collecte le résultat
    let sistersResult: SisterResult = fei.model.cellMatrixEngine.getSisterResultByPkey(fei.visionStepLevel, line.scen, measureFullKey, sisKey);

    mmi.r = sistersResult?.r;
  }

  /* ################################################
          series (même niveau)
    ################################################ */

  /**
   * construction d'une clé de série sur la WidgetLine
   * @param wl 
   * @param fkd, le champ qui sert pour la série
   * @param measureFullKey 
   */
  private static feedSerieKey(line: WidgetLine, fkds: FeiKeyDef[], measureFullKey: string): string {
    if(!line.serieKeyMap?.has(measureFullKey)) { // pas encore construite pour cette série, création
      // je crée ma clé wildCard
      if (!line.bl.splittedKey) {
        line.bl.splittedKey = line.bl.key.split(WizConstants.KEY_SEPARATOR);
      }
      let mySisKeyPart: string[] = [...line.bl.splittedKey];

      fkds.forEach((fkd: FeiKeyDef) => {
        mySisKeyPart[fkd.indexInKey] = WizConstants.wildcardDomain;
      });

      if(!line.serieKeyMap) {
        line.serieKeyMap = new Map();
      }
      line.serieKeyMap.set(measureFullKey, mySisKeyPart.join(WizConstants.KEY_SEPARATOR));     
    }
    
    // ma clé de sister avec son *
    return line.serieKeyMap.get(measureFullKey);
  }

  /**
   * alimentation des clés de series sur les WidgetLines si nécessaire
   * et retourne le pool qui correspond à ma clé de sisters
   * et triée en plus...
   * @param fei 
   * @param serieFKD le FKD clé pour la série
   * @param measureFullKey la clé unique de série
   * @param sisKey ma clé de soeur
   * @returns moi et mes soeurs
   */
  private static feedAllFeiSerieKeys(fei: FeiVO, serieFKDs: FeiKeyDef[], measureFullKey: string, sisKey: string): WidgetLine[] {
    let pool: WidgetLine[] = [];
    // on fait un tour de boucle pour alimenter les sisKeys sur les WidgetLines une seule fois
    let wlSerieKey: string;
    fei.getAllLines().forEach((wl: WidgetLine) => {       
      wlSerieKey = this.feedSerieKey(wl, serieFKDs, measureFullKey);
      if(wlSerieKey === sisKey) { // c'est ma soeur
        pool.push(wl);
      }
    });

    // préparation du tri, collecte du bon champ.
    // en cas de chrono, il en reste peut-être un plus précis que celui qui me sert de clé
    let realSortFkd: FeiKeyDef = serieFKDs[serieFKDs.length - 1]; // on prend le dernier élément
    if(realSortFkd.isChrono) {
      fei.visionKeys.forEach(vk => {
        if(vk.isChrono && vk.indexInKey > realSortFkd.indexInKey) {
          realSortFkd = vk;
        }
      });
    }

    // application du tri, une seule fois
    pool.sort(
      (a: WidgetLine, b: WidgetLine) =>
        BaselineEngine.getSort(fei, a, realSortFkd.fullCode, WizConstants.FIELD_LABEL) -
        BaselineEngine.getSort(fei, b, realSortFkd.fullCode, WizConstants.FIELD_LABEL)
    );

    return pool;
  }

  /**
   * alimentation d'une série de valeurs, depuis une base
   * On remplace un élément de clé linéaire par *
   * ça donne la clé de sisters
   * L'avantage, c'est qu'il n'y a pas d'update
   * Il ne peut y avoir de modification que sur create / delete
   *
   * @param refField le champ linéaire à remplacer, il forme l'assise de la série
   * @param refGeoFormula une valeur pour application d'une progression géométrique, 1 ne fait rien
   * @param refAriFormula une valeur pour application d'une progression arithmétique, 0 ne fait rien
   * @param baseValue valeur de base pour le 1er élément
   */
  public static SERIES(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {
    const refGeoFormula: MeasModeItem = mmi.c[1]; // formule géométrique
    const refAriFormula: MeasModeItem = mmi.c[2]; // formule arithmétique
    const baseValue: MeasModeItem = mmi.c[3]; // la valeur de base du 1er élément

    // les éléments linéaires
    const linearFields: string[] = mmi.c[0].form.replace(new RegExp('"', 'g'), '').split(WizConstants.KEY_SEPARATOR); // on extrait le champ de série; // le champ de référence linéaire
    // on check que ça existe
    let serieError: boolean = false;
    let serieFKDs: FeiKeyDef[] = [];//  fei.keyMap.get(linearField);
    linearFields.forEach(str => {
      let serieFKD: FeiKeyDef = fei.keyMap.get(str);
      if(!serieFKD) {
        serieError = true;
      } else {
        serieFKDs.push(serieFKD);
      }
    });
    
    // erreur sur les éléments de série, sortie
    if(serieError) {
      return;
    }

    // clé unique de ma clé linéaire
    let serieKey: string = linearFields.join(WizConstants.FD_PARTS_SEPARATOR);


    // la clé de regroupement global (measureFullKey), on marque la série avec @@SER|mesurecible|mmi.measureMode.code
    let measureFullKey: string = [WizConstants.SERIE_CODE, serieKey, mmi.mm.code].join(WizConstants.KEY_SEPARATOR);
    
    // ma clé de sister avec son *
    let sisKey: string = this.feedSerieKey(line, serieFKDs, measureFullKey);

    let exist: boolean = fei.model.cellMatrixEngine.exitSisterResult(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    if(!exist) {

      // mes soeurs, triées
      let pool: WidgetLine[] = this.feedAllFeiSerieKeys(fei, serieFKDs, measureFullKey, sisKey);

      // la map complète pour cette série
      let sistersMap: Map<string, SisterResult> = new Map();
      
      // pour les tours de boucle
      let wl: WidgetLine;
      let cell: WidgetCell;
      let val: string = null;
      
      // construction de ma série
      // la map de résultat pour alimenter SisterResult.resultMap
      let sistersVals: Map<WidgetLine, string> = new Map();
        
      // les cellules qui porteront les résultats des calculs
      let sisterCells: WidgetCell[] = [];

      // on alimente les valeurs
      for (let index = 0; index < pool.length; index++) {
        wl = pool[index];
        if(index === 0) {
          val = FormulaCoreEngine.computeFreeMeasure(fei, baseValue.form, wl);
        } else {
          // construction de l'opération ax + b
          const realFormula = refGeoFormula.form + '*' + Number(val) + '+' + refAriFormula.form;
          val = FormulaCoreEngine.computeFreeMeasure(fei, realFormula, wl); 
        }

        cell = fei.model.cellMatrixEngine.getWidgetCell(wl, mmi.mm.code);
        // cell = fei.model.cellMatrixEngine.addCell(wl.level, wl.key, wl.scen, mmi.measureMode.code, null, true);
        sisterCells.push(cell); // alimentation des cellules
        sistersVals.set(wl, val); // alimentation de la map de résultats
      }

      let sisterResult: SisterResult = {
        l: line.level, sc: line.scen, magg: measureFullKey, sk: sisKey,
        rMap: sistersVals, // dans ce cas, on alimente la map, pas le result
        count: pool.length, sis: sisterCells, 
        mk: mmi.mm.code // pas de mesure source pour les series
      };
      sistersMap.set(sisKey, sisterResult);

      // et on alimente la matrice
      fei.model.cellMatrixEngine.setNeoSistersDependencies(fei.visionStepLevel, line.scen, measureFullKey, sistersMap);
    }

    // je collecte le résultat
    let sistersResult: SisterResult = fei.model.cellMatrixEngine.getSisterResultByPkey(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    mmi.r = sistersResult?.rMap?.get(line);
  }

  /**
   * consolidation sur la base des lignes antérieures (même step, échelle chrono)
   * @param 1         CHRONO#level, mode string, séparés par |
   * @param 2         mesure cible
   * @param 3         méthode d'agrégat, mode string
   * @param 4         indice de départ (par rapport à moi)
   * @param 5         nbr d'éléments à prendre
   */
  public static SLIDEAGG(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {

    // les éléments linéaires
    const linearFields: string[] = mmi.c[0].form.replace(new RegExp('"', 'g'), '').split(WizConstants.KEY_SEPARATOR); // on extrait le champ de série; // le champ de référence linéaire
    // on check que ça existe
    let serieError: boolean = false;
    let serieFKDs: FeiKeyDef[] = [];//  fei.keyMap.get(linearField);
    linearFields.forEach(str => {
      let serieFKD: FeiKeyDef = fei.keyMap.get(str);
      if(!serieFKD) {
        serieError = true;
      } else {
        serieFKDs.push(serieFKD);
      }
    });
    
    // erreur sur les éléments de série, sortie
    if(serieError) {
      return;
    }

    // clé unique de ma clé linéaire
    let serieKey: string = linearFields.join(WizConstants.FD_PARTS_SEPARATOR);

    // la mesure cible à collecter sur les soeurs
    const measureKey: string = mmi.c[1].form.replace(new RegExp('"', 'g'), ''); 
    // on check que ça existe
    let measureFKD: FeiKeyDef = fei.keyMap.get(measureKey);
    if(!measureFKD) {
      return;
    }

    // la fonction d'aggrégat à jouer
    let aggregateKey: string = mmi.c[2].form?.replace(new RegExp('"', 'g'), ''); 
    if (!aggregateKey) {
      if(fei.model.dataEngine.datakindIsNumeric(measureFKD.datakind)) {
        aggregateKey = 'SUM';
      } else {
        aggregateKey = 'COUNT';
      }
    }

    // la clé de regroupement global (measureFullKey), on marque la série avec @@SER|mesurecible|mmi.measureMode.code
    let measureFullKey: string = [WizConstants.SERIE_CODE, serieKey, mmi.mm.code].join(WizConstants.KEY_SEPARATOR);
   
    // ma clé de sister avec son *
    let sisKey: string = this.feedSerieKey(line, serieFKDs, measureFullKey);

    let exist: boolean = fei.model.cellMatrixEngine.exitSisterResult(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    
    
    // let code: string = mmi.mm.code;
    // if(code === 'VTE_TY_CANET_XSEM#I' && sisKey === 'H24|100|*|*|100|100_101|100_101_103|100_101_103_100' && !exist) {
    //   // console.log('got first', code, line.key);
    //   // console.log('startParam', startParam, 'vectorParam', vectorParam);
    //   // if(line.key.startsWith('H24|100|H24_101|H24_101_101|100|100_101|100_101_103')) {
    //   //   // H24|100|H24_105|H24_105_101|100|100_101|100_101_101|100_101_101_101
    //   //   console.log('got you', code, line.key, measureFullKey, sisKey);
    //   // }
    // }

    
    if(!exist) {
      
      // la map complète pour cette série
      let sistersMap: Map<string, SisterResult> = new Map();
      
      // mes soeurs, triées
      let pool: WidgetLine[] = this.feedAllFeiSerieKeys(fei, serieFKDs, measureFullKey, sisKey);
      // les valeurs de mes soeurs, triées
      let values: string[] = [];

      // collecte des valeurs, dans l'ordre de la pile du pool

      pool.forEach((wl: WidgetLine) => {

        // let cell = fei.model.cellMatrixEngine.getWidgetCell(wl, mmi.mm.code);
        // val : prendre le scenar sur la vraie cellule de la widgetLine
        let fromCell: WidgetCell = fei.model.cellMatrixEngine.getWidgetCell(wl, measureKey);
        let val: string = fei.model.cellMatrixEngine.getCellValue(fromCell.l, fromCell.k, fromCell.sc, measureKey);
        // if(fei.scenario !== 'MAIN') {
        //   console.log('slideAgg', fromCell, val);
        // } 
        values.push(val);
      });

      // construction de ma série
      // la map de résultat pour alimenter SisterResult.resultMap
      let sistersVals: Map<WidgetLine, string> = new Map();
        
      // les cellules qui porteront les résultats des calculs
      let sisterCells: WidgetCell[] = [];

      // éléments de calcul
      let myIndex: number = -1; // ma position actuelle dans la pile (par défaut, "pas trouvé")
      let startIndex: number = 0; // indice de démarrage de collecte
      let endIndex: number = 0; // indice de fin de collecte
      let realStart;
      let realEnd;
      let valSlice: string[];
      let cell: WidgetCell;

      // le point de démarrage par rapport à ... moi
      let startParam: number = Number(mmi.c[3].r);

      // combien j'en collecte (+/- x)
      
      // prise des dépendances sur les semaines : on va chercher la formule OU ses dépendances
      let c4DepIsMeasure: boolean = false;
      let c4DepIsFormula: boolean = false;
      let c4Form: string = mmi.c[4].form;

      if(mmi.mm.deps?.has(c4Form)) {
        c4DepIsMeasure = true;
      } else if(!!c4Form && isNaN(Number(c4Form))) {
        c4DepIsFormula = true;
      }

      // on calcule toutes les lignes de la série
      pool.forEach((wl: WidgetLine) => {

        let vectorParam: number; 
        if(c4DepIsMeasure) {
          let vectorParamCell: WidgetCell = fei.model.cellMatrixEngine.getWidgetCell(wl, measureKey);
          let val: string = fei.model.cellMatrixEngine.getCellValue(vectorParamCell.l, vectorParamCell.k, vectorParamCell.sc, mmi.c[4].form);                  
          vectorParam = Number(val) || 0;
        } else if(c4DepIsFormula) {
          let res: string = FormulaCoreEngine.computeFreeMeasure(fei, c4Form, wl);
          // console.log('c4DepIsFormula', res, wl.key);
          vectorParam = Number(res) || 0; // TODO TODO
        } else {
          vectorParam = Number(mmi.c[4].r) || 0; 
        }


        myIndex = pool.indexOf(wl);
        startIndex = myIndex + startParam;
        endIndex = startIndex + vectorParam;
        // contrôles de cohérence
        realStart = Math.min(startIndex, endIndex); // on les passe dans le bon ordre
        realEnd = Math.max(startIndex, endIndex);
        if (realStart < 0) {
          realStart = 0;
        }
        if (realEnd < 0) {
          realEnd = 0;
        }
        valSlice = values.slice(realStart, realEnd);
        // on recycle le mmi en cours... pas bien
        FunctionCoreEngine.callFunction(aggregateKey, mmi, line, fei, valSlice);
        cell = fei.model.cellMatrixEngine.getWidgetCrossStepCell(wl, mmi.mm.code);
        sisterCells.push(cell); // alimentation des cellules
        sistersVals.set(wl, mmi.r); // alimentation de la map de résultats
      })

      let otherDeps: string[] = null;
      mmi.mm.deps?.forEach(s => {
        if(s !== measureKey) {
          if(!otherDeps) {
            otherDeps = [];
          }
          otherDeps.push(s);
        }
      });

      let sisterResult: SisterResult = {
        l: line.level, sc: line.scen, magg: measureFullKey, sk: sisKey,
        rMap: sistersVals, // dans ce cas, on alimente la map, pas le result 
        count: pool.length, sis: sisterCells, mk:measureKey, ok: otherDeps };
      sistersMap.set(sisKey, sisterResult);
      

      // et on alimente la matrice
      // ici, pour CHAQUE mesure de dépendance, pas seulement la mesure cible
      fei.model.cellMatrixEngine.setNeoSistersDependencies(fei.visionStepLevel, line.scen, measureFullKey, sistersMap);

    }


    // je collecte le résultat
    let sistersResult: SisterResult = fei.model.cellMatrixEngine.getSisterResultByPkey(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    mmi.r = sistersResult?.rMap?.get(line);

    // if(line.key === "TEN2_101|TEN2_101_104") {
    //   console.log('forKey', line, sistersResult, mmi.r);
    // }

  }

  /**
   * consolidation sur la base des lignes antérieuresdepuis un point de départ (même step, échelle chrono)
   * @param 1         CHRONO#level, mode string
   * @param 2         mesure cible
   * @param 3         méthode d'agrégat, mode string
   * @param 4         indice de départ
   */
  public static FROMAGG(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {
    
    // les éléments linéaires
    const linearFields: string[] = mmi.c[0].form.replace(new RegExp('"', 'g'), '').split(WizConstants.KEY_SEPARATOR); // on extrait le champ de série; // le champ de référence linéaire
    // on check que ça existe
    let serieError: boolean = false;
    let serieFKDs: FeiKeyDef[] = [];//  fei.keyMap.get(linearField);
    linearFields.forEach(str => {
      let serieFKD: FeiKeyDef = fei.keyMap.get(str);
      if(!serieFKD) {
        serieError = true;
      } else {
        serieFKDs.push(serieFKD);
      }
    });
    
    // erreur sur les éléments de série, sortie
    if(serieError) {
      return;
    }

    // clé unique de ma clé linéaire
    let serieKey: string = linearFields.join(WizConstants.FD_PARTS_SEPARATOR);


    // la mesure cible à collecter sur les soeurs
    const measureKey: string = mmi.c[1].form.replace(new RegExp('"', 'g'), ''); 
    // on check que ça existe
    let measureFKD: FeiKeyDef = fei.keyMap.get(measureKey);
    if(!measureFKD) {
      return;
    }

    // la fonction d'aggrégat à jouer
    let aggregateKey: string = mmi.c[2].form?.replace(new RegExp('"', 'g'), ''); 
    if (!aggregateKey) {
      if(fei.model.dataEngine.datakindIsNumeric(measureFKD.datakind)) {
        aggregateKey = 'SUM';
      } else {
        aggregateKey = 'COUNT';
      }
    }

    // le point de démarrage par rapport à ... moi
    const startParam: number = Number(mmi.c[3].r); 

    // la clé de regroupement global (measureFullKey), on marque la série avec @@SER|mesurecible|mmi.measureMode.code
    let measureFullKey: string = [WizConstants.SERIE_CODE, serieKey, mmi.mm.code].join(WizConstants.KEY_SEPARATOR);
   
    // ma clé de sister avec son *
    let sisKey: string = this.feedSerieKey(line, serieFKDs, measureFullKey);

    let exist: boolean = fei.model.cellMatrixEngine.exitSisterResult(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    if(!exist) {
      
      // la map complète pour cette série
      let sistersMap: Map<string, SisterResult> = new Map();
      
      // mes soeurs, triées
      let pool: WidgetLine[] = this.feedAllFeiSerieKeys(fei, serieFKDs, measureFullKey, sisKey);
      // les valeurs de mes soeurs, triées
      let values: string[] = [];

      // collecte des valeurs, dans l'ordre de la pile du pool
      pool.forEach((wl: WidgetLine) => {
        let cell = fei.model.cellMatrixEngine.getWidgetCell(wl, mmi.mm.code);
        let val: string = fei.model.cellMatrixEngine.getCellValue(cell.l, cell.k, cell.sc, measureKey);
        // let val: string = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, measureKey);
        values.push(val);
      });

      // construction de ma série
      // la map de résultat pour alimenter SisterResult.resultMap
      let sistersVals: Map<WidgetLine, string> = new Map();
        
      // les cellules qui porteront les résultats des calculs
      let sisterCells: WidgetCell[] = [];

      // éléments de calcul
      let myIndex: number = -1; // ma position actuelle dans la pile (par défaut, "pas trouvé")
      let startIndex: number = 0; // indice de démarrage de collecte
      let endIndex: number = 0; // indice de fin de collecte
      let realStart;
      let realEnd;
      let valSlice: string[];
      let cell: WidgetCell;

      // on calcule toutes les lignes de la série
      pool.forEach((wl: WidgetLine) => {
        myIndex = pool.indexOf(wl);
        startIndex = startParam - 1;
        endIndex = myIndex;
        // contrôles de cohérence
        realStart = startIndex; // on les passe dans le bon ordre
        realEnd = endIndex
        if (realStart < 0) {
          realStart = 0;
        }
        if (realEnd < 0) {
          realEnd = 0;
        }
        valSlice = values.slice(realStart, realEnd);
        // on recycle le mmi en cours... pas bien
        FunctionCoreEngine.callFunction(aggregateKey, mmi, line, fei, valSlice);
        cell = fei.model.cellMatrixEngine.getWidgetCell(wl, mmi.mm.code);
        sisterCells.push(cell); // alimentation des cellules
        sistersVals.set(wl, mmi.r); // alimentation de la map de résultats
      })

      let sisterResult: SisterResult = {
        l: line.level, sc: line.scen, magg: measureFullKey, sk: sisKey,
        rMap: sistersVals, // dans ce cas, on alimente la map, pas le result 
        count: pool.length, sis: sisterCells, mk: measureKey };
      sistersMap.set(sisKey, sisterResult);
      // et on alimente la matrice
      fei.model.cellMatrixEngine.setNeoSistersDependencies(fei.visionStepLevel, line.scen, measureFullKey, sistersMap);

    }

    // je collecte le résultat
    let sistersResult: SisterResult = fei.model.cellMatrixEngine.getSisterResultByPkey(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    mmi.r = sistersResult?.rMap?.get(line);
  }

  /**
   * collecte de la dernière valeur connue pour moi et mes soeurs (même pkey), 
   * ordonnées (par champ linéaire), 
   * et AVEC Valeur pour le champ concerné
   *
   * @param 1         CHRONO#level, mode string
   * @param 2         mesure cible
   */
  public static LASTVALUEOF(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {
    
    // les éléments linéaires
    const linearFields: string[] = mmi.c[0].form.replace(new RegExp('"', 'g'), '').split(WizConstants.KEY_SEPARATOR); // on extrait le champ de série; // le champ de référence linéaire
    // on check que ça existe
    let serieError: boolean = false;
    let serieFKDs: FeiKeyDef[] = [];//  fei.keyMap.get(linearField);
    linearFields.forEach(str => {
      let serieFKD: FeiKeyDef = fei.keyMap.get(str);
      if(!serieFKD) {
        serieError = true;
      } else {
        serieFKDs.push(serieFKD);
      }
    });
    
    // erreur sur les éléments de série, sortie
    if(serieError) {
      return;
    }

    // clé unique de ma clé linéaire
    let serieKey: string = linearFields.join(WizConstants.FD_PARTS_SEPARATOR);

    // la mesure cible à collecter sur les soeurs
    const measureKey: string = mmi.c[1].form.replace(new RegExp('"', 'g'), ''); 
    // on check que ça existe
    let measureFKD: FeiKeyDef = fei.keyMap.get(measureKey);
    if(!measureFKD) {
      return;
    }

    // la clé de regroupement global (measureFullKey), on marque la série avec @@SER|mesurecible|mmi.measureMode.code
    let measureFullKey: string = [WizConstants.SERIE_CODE, serieKey, mmi.mm.code].join(WizConstants.KEY_SEPARATOR);
  
    // ma clé de sister avec son *
    let sisKey: string = this.feedSerieKey(line, serieFKDs, measureFullKey);

    let exist: boolean = fei.model.cellMatrixEngine.exitSisterResult(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    if(!exist) {
      
      // la map complète pour cette série
      let sistersMap: Map<string, SisterResult> = new Map();
      
      // mes soeurs, triées
      let pool: WidgetLine[] = this.feedAllFeiSerieKeys(fei, serieFKDs, measureFullKey, sisKey);

      // les cellules qui porteront les résultats des calculs
      let sisterCells: WidgetCell[] = [];

      let val: string = null;
      let cell: WidgetCell;
      // on cherche la dernière valeur existante
      pool.forEach((wl: WidgetLine) => {
        
        cell = fei.model.cellMatrixEngine.getWidgetCell(wl, mmi.mm.code);
        sisterCells.push(cell);
        let protoVal = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, measureKey);
        if(protoVal?.length > 0) {
          val = protoVal;
        }
      })

      let sisterResult: SisterResult = {
        l: line.level, sc: line.scen, magg: measureFullKey, sk: sisKey,
        r: val, // un seul result, dernière valeur connue
        count: pool.length, sis: sisterCells, mk: measureKey };
      sistersMap.set(sisKey, sisterResult);
      // et on alimente la matrice
      fei.model.cellMatrixEngine.setNeoSistersDependencies(fei.visionStepLevel, line.scen, measureFullKey, sistersMap);

    }

    // je collecte le résultat
    let sistersResult: SisterResult = fei.model.cellMatrixEngine.getSisterResultByPkey(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    mmi.r = sistersResult?.r;
  }

  /**
   * collecte du dernier indice de valeur connue pour moi et mes soeurs (même pkey), 
   * AVEC l'indice de dernier élément pour le champ concerné
   *
   * @param 1         CHRONO#level, mode string
   * @param 2         mesure cible
   */
  public static LASTINDEXOF(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {
   
    // les éléments linéaires
    const linearFields: string[] = mmi.c[0].form.replace(new RegExp('"', 'g'), '').split(WizConstants.KEY_SEPARATOR); // on extrait le champ de série; // le champ de référence linéaire
    // on check que ça existe
    let serieError: boolean = false;
    let serieFKDs: FeiKeyDef[] = [];//  fei.keyMap.get(linearField);
    linearFields.forEach(str => {
      let serieFKD: FeiKeyDef = fei.keyMap.get(str);
      if(!serieFKD) {
        serieError = true;
      } else {
        serieFKDs.push(serieFKD);
      }
    });
    
    // erreur sur les éléments de série, sortie
    if(serieError) {
      return;
    }

    // clé unique de ma clé linéaire
    let serieKey: string = linearFields.join(WizConstants.FD_PARTS_SEPARATOR);

    // la mesure cible à collecter sur les soeurs
    const measureKey: string = mmi.c[1].form.replace(new RegExp('"', 'g'), ''); 
    // on check que ça existe
    let measureFKD: FeiKeyDef = fei.keyMap.get(measureKey);
    if(!measureFKD) {
      return;
    }

    // la clé de regroupement global (measureFullKey), on marque la série avec @@SER|mesurecible|mmi.measureMode.code
    let measureFullKey: string = [WizConstants.SERIE_CODE, serieKey, mmi.mm.code].join(WizConstants.KEY_SEPARATOR);
  
    // ma clé de sister avec son *
    let sisKey: string = this.feedSerieKey(line, serieFKDs, measureFullKey);

    let exist: boolean = fei.model.cellMatrixEngine.exitSisterResult(fei.visionStepLevel, fei.scenario, measureFullKey, sisKey);
    if(!exist) {
      
      // la map complète pour cette série
      let sistersMap: Map<string, SisterResult> = new Map();
      
      // mes soeurs, triées
      let pool: WidgetLine[] = this.feedAllFeiSerieKeys(fei, serieFKDs, measureFullKey, sisKey);

      // les cellules qui porteront les résultats des calculs
      let sisterCells: WidgetCell[] = [];

      let val: string = null;
      let cell: WidgetCell;

      // on cherche la dernière valeur existante
      for (let index = 0; index < pool.length; index++) {
        const wl: WidgetLine = pool[index];
        
        cell = fei.model.cellMatrixEngine.getWidgetCell(wl, mmi.mm.code);
        sisterCells.push(cell);
        let protoVal = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, measureKey);
        if(protoVal?.length > 0) {
          val = (index + 1).toString();
        }
      }

      let sisterResult: SisterResult = {
        l: line.level, sc: line.scen, magg: measureFullKey, sk: sisKey,
        r: val, // un seul result, dernière valeur connue
        count: pool.length, sis: sisterCells, mk: measureKey };
      sistersMap.set(sisKey, sisterResult);
      // et on alimente la matrice
      fei.model.cellMatrixEngine.setNeoSistersDependencies(fei.visionStepLevel, line.scen, measureFullKey, sistersMap);

    }

    // je collecte le résultat
    let sistersResult: SisterResult = fei.model.cellMatrixEngine.getSisterResultByPkey(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    mmi.r = sistersResult?.r;
  }

  /**
   * ventilation de la valeur du parent sur les enfants
   * @param measureKey la mesure sur la ligne mère
   * @param weight (formule) ce qui donne le poids de la ligne en cours
   * soit une weightGrid, soit une mesure
   * @returns ma part dans la ligne mère, en fonction de mon poids
   */
  public static SPLIT(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {

    // retour par défaut
    mmi.r = null;
 
    if(line.level === 1) { // pas de soeurs au 1er niveau
      return;
    }

    // la mesure cible à collecter sur les soeurs
    const measureKey: string = mmi.c[0].form; 
    // on check que ça existe
    let serieFKD: FeiKeyDef = fei.keyMap.get(measureKey);
    if(!serieFKD) {
      return;
    }

    const requiredLevel: number = line.level - 1;
    const scenario: string = line.scen;

    // fei cible
    let motherFEI: FeiVO = this.getOtherFEI(fei, requiredLevel);
    if (!motherFEI?.computed) { // retour, pas chargé...
      mmi.r = ''; // j'ai pas ENCORE de parent...

      // faire ça une seule fois, comme les setCollections...
      if(!fei.model.cellMatrixEngine.pendingExist(requiredLevel, scenario, line.level, line.key, mmi.mm.code)) { 
        this.addMassPending(fei, mmi.mm.code, requiredLevel, measureKey);
      }
      return;
    }

    // baseline parente
    const motherLine: WidgetLine = motherFEI.getWidgetLineForKey(scenario, line.bl.pkey);

    if (!motherLine) {
      mmi.isErr = true;
      mmi.r = ''; // j'ai VRAIMENT pas de parent...
      return;
    }

    // la clé de regroupement global (measureFullKey), on marque la série avec @@SER|mesurecible|mmi.measureMode.code
    let measureFullKey: string = [WizConstants.SERIE_CODE, serieFKD.fullCode, mmi.mm.code].join(WizConstants.KEY_SEPARATOR);
    // la clé de regroupement des soeurs, ventilation par pkey
    let sisKey: string = line.bl.pkey;

    let exist: boolean = fei.model.cellMatrixEngine.exitSisterResult(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    if(!exist) {
      
      // mes soeurs (et moi)
      let pool: WidgetLine[] = [...fei.getWidgetLinesByLevel(scenario, requiredLevel, sisKey)?.values()];

      // la map complète pour cette série
      let sistersMap: Map<string, SisterResult> = new Map();
      
      // pour les tours de boucle
      let wl: WidgetLine;
      let cell: WidgetCell;
 
      // construction de ma série
      // la map de résultat pour alimenter SisterResult.resultMap
      let sistersVals: Map<WidgetLine, string> = new Map();
        
      // les cellules qui porteront les résultats des calculs
      let sisterCells: WidgetCell[] = [];

      // ## on calcule
      // prise de la valeur sur la mère
      let motherValue: string = fei.model.cellMatrixEngine.getCellValue(requiredLevel, sisKey, motherLine.bl.scen, measureKey);

      // poids total
      let totalWeight: number = 0;
      // poids de la ligne en cours
      let lineWeight: string;

      // le second élément, qui donne le calcul du poids
      const weightKey: string = mmi.c[1].form; 

      // on alimente les poids pour chaque élément
      // et on consolide le poids total
      for (let index = 0; index < pool.length; index++) {
        wl = pool[index];

        // calcul du poids pour la ligne
        lineWeight = FormulaCoreEngine.computeFreeMeasure(fei, weightKey, wl) || '0';
        totalWeight += Number(lineWeight);

        cell = fei.model.cellMatrixEngine.getWidgetCell(wl, mmi.mm.code);
        sisterCells.push(cell); // alimentation des cellules
        sistersVals.set(wl, lineWeight); // alimentation de la map de résultats
      }

      // postControl, si totalWeight est à 0, on le passe à 1
      if(totalWeight === 0) {
        totalWeight = 1;
      }

      let sisterResult: SisterResult = {
        l: line.level, sc: line.scen, magg: measureFullKey, sk: sisKey,
        r: motherValue, // la valeur de la ligne mère
        rMap: sistersVals, // les poids par enfants
        weight: totalWeight, // le poids total
        count: pool.length, sis: sisterCells, 
        mk: mmi.mm.code // pas de mesure source pour les series
      };
      sistersMap.set(sisKey, sisterResult);

      // et on alimente la matrice
      fei.model.cellMatrixEngine.setNeoSistersDependencies(fei.visionStepLevel, line.scen, measureFullKey, sistersMap);

      // ajout de la dépendance sur la cellule mère
      let motherCell: IBaselineValue = fei.model.cellMatrixEngine.addCell(requiredLevel,sisKey, motherLine.bl.scen, measureKey, null, true);
      fei.model.cellMatrixEngine.addSisterReference(motherCell, sisterResult);

    }

    // je collecte le résultat
    let sistersResult: SisterResult = fei.model.cellMatrixEngine.getSisterResultByPkey(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
    if(sistersResult) {
      let motherValue: number = Number(sistersResult.r);
      let totalWeight: number = Number(sistersResult.weight);
      let lineWeight: number = Number(sistersResult.rMap.get(line));
      let result: number = Math.round((motherValue * lineWeight * 100000) / totalWeight) / 100000;
      mmi.r = result?.toString();
    }
  }

  /**
   * contrôle de chevauchement
   * =COMPARERANGE("PRODUCT#1";OFFER_BINKEY#I;"OPE#1|PRODUCT#1";STARTDATE#I;ENDDATE#I)
   * 
   * @param groupKey les clés de champ pour ventilation, séparées par |
   * je vais prendre ces valeurs de codif pour former une partie de ma clé
   * 
   * @param binKey une clé de champ binaire, pour faire des regroupements de valeurs
   * je vais aussi prendre cette valeur pour former ma clé. Ici, c'est une mesure (int). je vais passer une puissance dessus (Pow(2,val))
   *  par exemple, si un champ contient les valeurs 1, 2 et 3 pour différents éléments, 1 et 3 sont "commun", 2 et 3 aussi. Mais pas 1 et 2
   * 
   * 1 + 2 me donne ma "clé de recherche", ça forme des "ensembles"
   *
   * @param identityKey me donne ma clé "d'unicité" les clés de champ pour former l'unicité de ligne, séparées par |, ça donne la clé "unique" de ma ligne
   *
   * Je ne dois pas obtenir 2 fois ma clé d'unicité pour une même clé de recherche, sur la période
   *
   * @param startDate champ date de début
   * @param endDate champ date de fin
   * @return 1 s'il existe plus d'un élément (identityKey) dans l'ensemble
   */
  public static COMPARERANGE(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {

    mmi.r = null;

    // création de la structure du pack
    // la clé de groupe
    const groupKey: string[] = mmi.c[0].form?.replace(new RegExp('"', 'g'), '').split(WizConstants.KEY_SEPARATOR); // on extrait le champ de série
    
    // les FKD qui donnent la clé d'ensemble
    let keyError: boolean = false;
    const groupFKD: FeiKeyDef[] = [];
    groupKey.forEach((key: string) => {
      // on check que ça existe
      let keyFKD: FeiKeyDef = fei.keyMap.get(key);
      if(!keyFKD) {
        keyError = true;
      } else {
        groupFKD.push(keyFKD);
      }
    });
    if(keyError) {
      return; 
    }

    // la clé d'identité
    const identityKey: string[] = mmi.c[2].form?.replace(new RegExp('"', 'g'), '').split(WizConstants.KEY_SEPARATOR); // on extrait les champs d'unicité

    // les FKD qui donnent la clé d'identité
    const identityFKD: FeiKeyDef[] = [];
    identityKey.forEach((key: string) => {
      // on check que ça existe
      let keyFKD: FeiKeyDef = fei.keyMap.get(key);
      if(!keyFKD) {
        keyError = true;
      } else {
        identityFKD.push(keyFKD);
      }
    });
    if(keyError) {
      return; 
    } 

    
    // la clé binaire
    const binKey: string = mmi.c[1].form?.replace(new RegExp('"', 'g'), ''); // on extrait les clés binaires

    // champs date
    const startField: string = mmi.c[3]?.form;
    const endField: string = mmi.c[4]?.form;

    // clé du pack
    const packKey: string = [groupKey, binKey, identityKey, startField, endField].join(WizConstants.KEY_SEPARATOR);

    // construction unique pour l'ensemble des packs, à cause de la binKey (chevauchements possibles)
    let exist: boolean = fei.model.cellMatrixEngine.exitSisterResult(fei.visionStepLevel, fei.scenario, packKey, WizConstants.wildcardDomain);
    
    // let myIdentityPart: string[] = [];
    // identityFKD.forEach((fkd: FeiKeyDef) => {
    //     myIdentityPart.push(line.bl.splittedKey[fkd.indexInKey]);
    // }); 
    // let id: string = myIdentityPart.join(WizConstants.KEY_SEPARATOR);
    // if(id === 'PRECO_27|102760') {
    //   console.log('gotYou', line,  mmi.measureMode.code);
    // }

    if(!exist) { // construction du pack
      CompareManager.generatePack(packKey, mmi.mm.code, fei, groupFKD, binKey, identityFKD, startField, endField);
    }

    // je collecte le résultat
    let sistersResult: SisterResult = fei.model.cellMatrixEngine.getSisterResultByPkey(fei.visionStepLevel, fei.scenario, packKey, WizConstants.wildcardDomain);
    mmi.r = sistersResult?.rMap?.get(line);

  }

  /**
   * contrôle de chevauchement. Erreur en cas de valeurs différentes sur une même clé d'unicité
   * =COMPARERANGE("PRODUCT#1";OFFER_BINKEY#I;"OPE#1|PRODUCT#1";MEAS#I;STARTDATE#I;ENDDATE#I)
   * 
   * @param groupKey les clés de champ pour ventilation, séparées par |
   * je vais prendre ces valeurs de codif pour former une partie de ma clé
   * 
   * @param binKey une clé de champ binaire, pour faire des regroupements de valeurs
   * je vais aussi prendre cette valeur pour former ma clé. Ici, c'est une mesure (int). je vais passer une puissance dessus (Pow(2,val))
   *  par exemple, si un champ contient les valeurs 1, 2 et 3 pour différents éléments, 1 et 3 sont "commun", 2 et 3 aussi. Mais pas 1 et 2
   * 
   * 1 + 2 me donne ma "clé de recherche", ça forme des "ensembles"
   *
   * @param identityKey me donne ma clé "d'unicité" les clés de champ pour former l'unicité de ligne, séparées par |, ça donne la clé "unique" de ma ligne
   *
   * @param mesureKey Je ne dois pas obtenir 2 valeurs différentes pour cette mesure sur les éléments de ma clé d'unicité
   *
   * @param startDate champ date de début
   * @param endDate champ date de fin
   * @return 1 s'il existe plus d'un élément (identityKey) dans l'ensemble
   */

  public static FALSETWINS(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {

    mmi.r = null;

    // création de la structure du pack
    // la clé de groupe
    const groupKey: string[] = mmi.c[0].form?.replace(new RegExp('"', 'g'), '').split(WizConstants.KEY_SEPARATOR); // on extrait le champ de série
    
    // les FKD qui donnent la clé d'ensemble
    let keyError: boolean = false;
    const groupFKD: FeiKeyDef[] = [];
    groupKey.forEach((key: string) => {
      // on check que ça existe
      let keyFKD: FeiKeyDef = fei.keyMap.get(key);
      if(!keyFKD) {
        keyError = true;
      } else {
        groupFKD.push(keyFKD);
      }
    });
    if(keyError) {
      return; 
    }

    // la clé d'identité
    const identityKey: string[] = mmi.c[2].form?.replace(new RegExp('"', 'g'), '').split(WizConstants.KEY_SEPARATOR); // on extrait les champs d'unicité

    // les FKD qui donnent la clé d'identité
    const identityFKD: FeiKeyDef[] = [];
    identityKey.forEach((key: string) => {
      // on check que ça existe
      let keyFKD: FeiKeyDef = fei.keyMap.get(key);
      if(!keyFKD) {
        keyError = true;
      } else {
        identityFKD.push(keyFKD);
      }
    });
    if(keyError) {
      return; 
    } 

    // la mesure cible qui doit être unique en cas de non-unicité
    const measureKey: string = mmi.c[3].form; 
    // on check que ça existe
    let twinFKD: FeiKeyDef = fei.keyMap.get(measureKey);
    if(!twinFKD) {
      return;
    }


    // la clé binaire
    const binKey: string = mmi.c[1].form?.replace(new RegExp('"', 'g'), ''); // on extrait les clés binaires

    // champs date
    const startField: string = mmi.c[4]?.form;
    const endField: string = mmi.c[5]?.form;

    // clé du pack
    const packKey: string = [groupKey, binKey, identityKey, measureKey, startField, endField].join(WizConstants.KEY_SEPARATOR);

    // construction unique pour l'ensemble des packs, à cause de la binKey (chevauchements possibles)
    let exist: boolean = fei.model.cellMatrixEngine.exitSisterResult(fei.visionStepLevel, fei.scenario, packKey, WizConstants.wildcardDomain);
    
    if(!exist) { // construction du pack
      CompareManager.generatePack(packKey, mmi.mm.code, fei, groupFKD, binKey, identityFKD, startField, endField, twinFKD);
    }

    // je collecte le résultat
    let sistersResult: SisterResult = fei.model.cellMatrixEngine.getSisterResultByPkey(fei.visionStepLevel, fei.scenario, packKey, WizConstants.wildcardDomain);
    mmi.r = sistersResult?.rMap?.get(line);
  }

    /* ################################################
            collections du dessous
     ################################################ */

  /**
   * consolidation des valeurs d'une mesure de type liste
   * Alimente le collectContent sur le mmi (collection de string)
   * @param1 : la mesure cible sur les enfants
   * @param mmi      Etape de calcul concernée
   * @param line: WidgetLine de travail concernée
   * @param fei      Moteur de calcul
   */
  public static COLLECTLIST(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {
    const rawList: MeasModeItem = mmi.c[0]; // le résultat de la mesure. C'est une liste (ou rien)
    const childrenValues: string[] = []; // la collection de valeurs collectées sur les enfants
    
    if(rawList?.r) {
      try {
        let rawobj: string[] = JSON.parse(rawList.r);
        Object.values(rawobj).forEach((s: string) => {
          if(s) {
            childrenValues.push(s);
          }
        });
      } catch(e: any) {
        console.log('invalid list value: ', rawList.r);
      }
    }
    mmi.cc = childrenValues;
  }




















  /**
   * alimentation d'une série de valeurs, depuis une base
   * On remplace un élément de clé linéaire par *
   * ça donne la clé de sisters
   * L'avantage, c'est qu'il n'y a pas d'update
   * Il ne peut y avoir de modification que sur create / delete
   *
   * @param refField le champ linéaire à remplacer, il forme l'assise de la série
   * @param refGeoFormula une valeur pour application d'une progression géométrique, 1 ne fait rien
   * @param refAriFormula une valeur pour application d'une progression arithmétique, 0 ne fait rien
   * @param baseValue valeur de base pour le 1er élément
   */
  public static GROUPVAL(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO): void {
    // les éléments linéaires (construction de la clé identitaire du groupe)
    const targetMeasure: string = mmi.c[0].form.replace(new RegExp('"', 'g'), '');
    const aggMode: string = mmi.c[1].form.replace(new RegExp('"', 'g'), ''); 
    
    const linearFields: string[] = [];
    // puis une pile de mesure
    for (let index = 2; index < mmi.c.length; index++) {
      linearFields.push(mmi.c[index].form.replace(new RegExp('"', 'g'), ''));
      
    }
    // const linearFields: string[] = mmi.c[1].form.replace(new RegExp('"', 'g'), '').split(WizConstants.KEY_SEPARATOR); // on extrait le champ de série; // le champ de référence linéaire

    // console.log('GROUPVAL', targetMeasure, linearFields, aggMode);

    // on check que ça existe
    let serieError: boolean = false;
    let serieFKDs: FeiKeyDef[] = [];//  fei.keyMap.get(linearField);
    linearFields.forEach(str => {
      let serieFKD: FeiKeyDef = fei.keyMap.get(str);
      if(!serieFKD) {
        serieError = true;
      } else {
        serieFKDs.push(serieFKD);
      }
    });
    
    // erreur sur les éléments de série, sortie
    if(serieError) {
      return;
    }

    // clé unique de ma clé linéaire
    let serieKey: string = linearFields.join(WizConstants.FD_PARTS_SEPARATOR);

    if(line.isTotalLine) { // gestion spécifique pour utilisation en aggrégat

      // la clé de regroupement global (measureFullKey), on marque la série avec @@SER|mesurecible|mmi.measureMode.code
      let measureFullKey: string = [WizConstants.SERIE_CODE, serieKey, mmi.mm.code].join(WizConstants.KEY_SEPARATOR);

      // if(sisKey === 'PE25|55|CF|104') {
      // console.log('GROUPVAL AGG_MODE', aggMode);
      // }


      let serieKeyMap: Map<string, number[]> = new Map();
      let sisKey: string = null;
      let val: string = null;
      [...line.ch?.values() || []].forEach(wl => {
        // ma clé de groupVal
        sisKey = this.feedGroupValKey(fei, wl, serieFKDs, measureFullKey);
        if(!serieKeyMap.has(sisKey)) {
          serieKeyMap.set(sisKey, []);
        }
        val = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.scen, targetMeasure);
        serieKeyMap.get(sisKey).push(Number(val));
      });

      // console.log('GROUPVAL AGG_MODE', serieKeyMap);

      let serieResult: number[] = [];
      let seriSum: number = null;
           
      [...serieKeyMap.values() || []].forEach((vals: number[]) => {
        let aggVal: number = 0;
        let aggNb: number = 0;
        vals.forEach(v => {
          if(aggMode === 'SUM') {
            aggVal += v;
          }
          else if(aggMode === 'AVG') {
            aggVal += v;
            aggNb++;
          }
          else if(aggMode === 'MIN') {
            if(v < aggVal) {
              aggVal = v;
            }
          }
          else if(aggMode === 'MAX') {
            if(v > aggVal) {
              aggVal = v;
            }
          }
          else if(aggMode === 'COUNT') {
            aggNb++;
          }
        });

        // fin de traitement, éventuel postJob
        if(aggMode === 'AVG' && aggNb > 0) {
              aggVal = aggVal / aggNb;
        } if(aggMode === 'COUNT') {
              aggVal = aggNb;
        }
        serieResult.push(aggVal);
        if(aggVal !== null ) {
          if(seriSum === null) {
            seriSum = aggVal;
          } else {
            seriSum += aggVal;
          }
        }

      });

      mmi.r = seriSum?.toString() || null;

    } else { // pas en aggrégat, fonction classique
      // la clé de regroupement global (measureFullKey), on marque la série avec @@SER|mesurecible|mmi.measureMode.code
      let measureFullKey: string = [WizConstants.SERIE_CODE, serieKey, mmi.mm.code].join(WizConstants.KEY_SEPARATOR);
      
      // ma clé de groupVal
      let sisKey: string = this.feedGroupValKey(fei, line, serieFKDs, measureFullKey);


      let exist: boolean = fei.model.cellMatrixEngine.exitSisterResult(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
      if(!exist) {

        // mes soeurs
        let pool: WidgetLine[] = this.feedAllGroupValKeys(fei, serieFKDs, measureFullKey, sisKey);

        // console.log('sisKey', sisKey, pool?.length);

        // la map complète pour cette série
        let sistersMap: Map<string, SisterResult> = new Map();
        
        // pour les tours de boucle
        let wl: WidgetLine;
        let cell: WidgetCell;
        let val: string = null;
        
        // construction de ma série
        // la map de résultat pour alimenter SisterResult.resultMap
        let sistersVals: Map<WidgetLine, string> = new Map();
          
        // les cellules qui porteront les résultats des calculs
        let sisterCells: WidgetCell[] = [];



        let vals: number[] = [];

        // on alimente les valeurs
        for (let index = 0; index < pool.length; index++) {
          wl = pool[index];
          // console.log('GROUPVAL', wl.key);
          val = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.scen, targetMeasure);
          vals.push(Number(val));

          cell = fei.model.cellMatrixEngine.getWidgetCell(wl, mmi.mm.code);
          // cell = fei.model.cellMatrixEngine.addCell(wl.level, wl.key, wl.scen, mmi.measureMode.code, null, true);
          sisterCells.push(cell); // alimentation des cellules
          sistersVals.set(wl, val); // alimentation de la map de résultats
        }

        let aggVal: number = 0;
        let aggNb: number = 0;
        vals.forEach(v => {
            if(aggMode === 'SUM') {
              aggVal += v;
            }
            else if(aggMode === 'AVG') {
              aggVal += v;
              aggNb++;
            }
            else if(aggMode === 'MIN') {
              if(v < aggVal) {
                aggVal = v;
              }
            }
            else if(aggMode === 'MAX') {
              if(v > aggVal) {
                aggVal = v;
              }
            }
            else if(aggMode === 'COUNT') {
              aggNb++;
            }
        });
        
        // fin de traitement, éventuel postJob
        if(aggMode === 'AVG' && aggNb > 0) {
              aggVal = aggVal / aggNb;
        } if(aggMode === 'COUNT') {
              aggVal = aggNb;
        }

        [...sistersVals.keys()].forEach(wl => {
          sistersVals.set(wl, aggVal.toString());
        });


        let sisterResult: SisterResult = {
          l: line.level, sc: line.scen, magg: measureFullKey, sk: sisKey,
          rMap: sistersVals, // dans ce cas, on alimente la map, pas le result
          count: pool.length, sis: sisterCells, 
          mk: mmi.mm.code // pas de mesure source pour les series
        };
        sistersMap.set(sisKey, sisterResult);

        // et on alimente la matrice
        fei.model.cellMatrixEngine.setNeoSistersDependencies(fei.visionStepLevel, line.scen, measureFullKey, sistersMap);
      }

      // je collecte le résultat
      let sistersResult: SisterResult = fei.model.cellMatrixEngine.getSisterResultByPkey(fei.visionStepLevel, line.scen, measureFullKey, sisKey);
      mmi.r = sistersResult?.rMap?.get(line);


    }


   
  }

  /**
   * construction d'une clé de série sur la WidgetLine
   * @param wl 
   * @param fkd, le champ qui sert pour la série
   * @param measureFullKey 
   */
  private static feedGroupValKey(fei: FeiVO, line: WidgetLine, fkds: FeiKeyDef[], measureFullKey: string): string {
    if(!line.serieKeyMap?.has(measureFullKey)) { // pas encore construite pour cette série, création
      
      // prise des valeurs assemblées par |
      let mySisKeyPart: string[] = [];
      
      fkds.forEach(fkd => {
        let val: string = BaselineEngine.getRawKeyOrValue(fei, line, fkd.fullCode);
        mySisKeyPart.push(val);
      });
      

      if(!line.serieKeyMap) {
        line.serieKeyMap = new Map();
      }
      // console.log('line', line.key, mySisKeyPart.join(WizConstants.KEY_SEPARATOR))
      line.serieKeyMap.set(measureFullKey, mySisKeyPart.join(WizConstants.KEY_SEPARATOR));     
    }
    
    // ma clé de sister
    return line.serieKeyMap.get(measureFullKey);
  }

  /**
   * alimentation des clés de series sur les WidgetLines si nécessaire
   * et retourne le pool qui correspond à ma clé de sisters
   * et triée en plus...
   * @param fei 
   * @param serieFKD le FKD clé pour la série
   * @param measureFullKey la clé unique de série
   * @param sisKey ma clé de soeur
   * @returns moi et mes soeurs
   */
  private static feedAllGroupValKeys(fei: FeiVO, serieFKDs: FeiKeyDef[], measureFullKey: string, sisKey: string): WidgetLine[] {
    let pool: WidgetLine[] = [];
    // on fait un tour de boucle pour alimenter les sisKeys sur les WidgetLines une seule fois
    let wlSerieKey: string;
    fei.getAllLines().forEach((wl: WidgetLine) => {       
      wlSerieKey = this.feedGroupValKey(fei, wl, serieFKDs, measureFullKey);
      if(wlSerieKey === sisKey) { // c'est ma soeur
        pool.push(wl);
      }
    });

    return pool;
  }






































  /** retourne le premier ou le dernier d'une série */
  private static getFromIndex(mmi: MeasModeItem, first: boolean, fei: FeiVO): void {
    // contrôle de la collection
    const fixedMmi: string[] = FunctionCoreEngine.collectToMmiContent(fei.model, mmi);
    let child: string = null;
    if (first) {
      child = fixedMmi[0];
    } else {
      child = fixedMmi[fixedMmi.length - 1];
    }
    mmi.r = child;
  }

  /**
   * retourne le 1er élément de la liste
   * prend une série comme paramètre
   * @param mmi Etape de calcul concernée
   */
  public static FIRST(mmi: MeasModeItem, fei: FeiVO): void {
    this.getFromIndex(mmi, true, fei);
  }

  /**
   * retourne le dernier de la liste
   * prend une série comme paramètre
   * @param mmi Etape de calcul concernée
   */
  public static LAST(mmi: MeasModeItem, fei: FeiVO): void {
    this.getFromIndex(mmi, false, fei);
  }

}
